import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import evaluationRoutes from "./evaluationRoutes";
import investigationRoutes from "./investigationRoutes";
import securityRoutes from "./securityRoutes";
import configurationRoutes from "./configurationRoutes";
import workflowRoutes from "./workflowRoutes";
import biddingRoutes from "./biddingRoutes";
import contractRoutes from "./contractRoutes";
import registrationRoutes from "./registrationRoutes";
import appealsRoutes from "./appealsRoutes";
import budgetingAndAppRoutes from "@/router/budgetingAndAppRoutes";
import procurementAuditRoutes from "@/router/procurementAuditRoutes";
import requisitionRoutes from "@/router/requisitionRoutes";
import procurementCommitteeRoutes from "@/router/procurementCommitteeRoutes";
import appConfig from "../../app.config";
import reverseAuctionRouter from "./reverseAuctionRouter";
import eLearningRoutes from "./eLearningRoutes";
import complianceAssessmentRoutes from "./complianceAssessmentRoutes";
import ocdsRoutes from "@/router/ocdsRoutes";
import reportSubmissionRoutes from "@/router/reportSubmissionRoutes";
import { analyticsRoutes, ANALYTICS_MODULES as M } from '@/analytics/routeAnalytics';

const allRoutes = [
  ...analyticsRoutes(null, routes),
  ...analyticsRoutes(M.COMPLIANCE, complianceAssessmentRoutes),
  ...analyticsRoutes(M.INVESTIGATIONS, investigationRoutes),
  ...analyticsRoutes(M.SECURITY, securityRoutes),
  ...analyticsRoutes(M.CONFIGURATION, configurationRoutes),
  ...analyticsRoutes(M.WORKFLOW, workflowRoutes),
  ...analyticsRoutes(M.BIDDING, biddingRoutes),
  ...analyticsRoutes(M.CONTRACT_MANAGEMENT, contractRoutes),
  ...analyticsRoutes(M.REGISTRATION, registrationRoutes),
  ...analyticsRoutes(M.EVALUATION, evaluationRoutes),
  ...analyticsRoutes(M.PROCUREMENT_PLANNING, budgetingAndAppRoutes),
  ...analyticsRoutes(M.PROCUREMENT_AUDIT, procurementAuditRoutes),
  ...analyticsRoutes(M.REQUISITIONS, requisitionRoutes),
  ...analyticsRoutes(M.APPEALS, appealsRoutes),
  ...analyticsRoutes(M.REVERSE_AUCTION, reverseAuctionRouter),
  ...analyticsRoutes(M.ADMINISTRATION, procurementCommitteeRoutes),
  ...analyticsRoutes(M.E_LEARNING, eLearningRoutes),
  ...analyticsRoutes(M.OCDS, ocdsRoutes, { trackAuthenticated: true }),
  ...analyticsRoutes(null, reportSubmissionRoutes),
];

const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  console.log(`[Router] Navigating to: ${routeTo.path} (Name: ${routeTo.name}) from ${routeFrom.path}`);

  // Public front door: logged-out visitors to the root dashboard get the public landing instead of
  // being bounced to login (the dashboard fires authenticated calls that 401 → logout). Logged-in
  // users are kept on the dashboard. Switches only on JWT presence, so no redirect loop.
  const sessionJwt = sessionStorage.getItem('jwt');
  if (routeTo.name === 'default' && (!sessionJwt || !sessionJwt.length)) {
    return next({ name: 'public-landing' });
  }
  if (routeTo.name === 'public-landing' && sessionJwt && sessionJwt.length) {
    return next({ name: 'default' });
  }

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  console.log(`[Router] Auth required: ${authRequired}`);

  if (!authRequired) {
    console.log(`[Router] No authentication required, proceeding to ${routeTo.path}`);
    return next();
  }

  const jwt = sessionStorage.getItem('jwt');
  console.log(`[Router] JWT: ${jwt ? 'Present' : 'Missing'}`);
  if (authRequired && (!jwt || !jwt.length)) {
    console.log(`[Router] Blocked: Missing or invalid JWT, redirecting to /login`);
    return next('/login');
  }

  const privilegesRequired = routeTo.matched.reduce((privileges, route) => {
    if (route.meta.privileges) {
      privileges.push(...route.meta.privileges);
    }
    return privileges;
  }, []);
  console.log(`[Router] Required privileges: ${JSON.stringify(privilegesRequired)}`);

  if (privilegesRequired.length) {
    let currentUserPrivileges = [];
    try {
      const privilegesData = sessionStorage.getItem('privileges');
      console.log(`[Router] Raw privileges data: ${privilegesData}`);
      if (privilegesData) {
        const parsedPrivileges = JSON.parse(privilegesData);
        // Extract 'value' or 'name' from privilege objects
        currentUserPrivileges = parsedPrivileges.map(privilege => privilege.value || privilege.name);
      }
    } catch (error) {
      console.error(`[Router] Blocked: Failed to parse privileges for ${routeTo.path}:`, error);
      console.log(`[Router] Redirecting to unauthorized due to privilege parsing error`);
      return next({ name: 'unauthorized', query: { redirectFrom: routeTo.fullPath } });
    }
    console.log(`[Router] User privileges: ${JSON.stringify(currentUserPrivileges)}`);

    const hasRequiredPrivileges = privilegesRequired.some(privilege => currentUserPrivileges.includes(privilege));
    if (!hasRequiredPrivileges) {
      console.log(`[Router] Blocked: User lacks required privileges for ${routeTo.path}, missing: ${JSON.stringify(privilegesRequired.filter(p => !currentUserPrivileges.includes(p)))}`);
      return next({ name: 'unauthorized', query: { redirectFrom: routeTo.fullPath } });
    }
  }

  console.log(`[Router] All checks passed, proceeding to ${routeTo.path}`);
  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  console.log(`[Router] beforeResolve for ${routeTo.path}`);
  try {
    for (const route of routeTo.matched) {
      if (route.meta && route.meta.beforeResolve) {
        console.log(`[Router] Executing beforeResolve for route: ${route.path}`);
        await new Promise((resolve, reject) => {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              console.log(`[Router] Blocked: beforeResolve redirected for ${routeTo.path}, args:`, args);
              next(...args);
              reject(new Error('Redirected'));
            } else {
              console.log(`[Router] beforeResolve passed for ${route.path}`);
              resolve();
            }
          });
        });
      } else {
        console.log(`[Router] No beforeResolve hook for ${route.path}`);
      }
    }
  } catch (error) {
    console.error(`[Router] Blocked: beforeResolve error for ${routeTo.path}:`, error);
    return;
  }
  const pageTitle = routeTo.meta.title || 'App';
  console.log(`[Router] Setting page title: ${pageTitle} | ${appConfig.title}`);
  document.title = `${pageTitle} | ${appConfig.title}`;
  next();
});

export default router;
